import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import Page from 'components/layouts/page/Page';
import Button from 'components/shared/Button';
import DatePicker from 'components/shared/form/DatePicker';
import FieldLabel from 'components/shared/form/FieldLabel';
import Input from 'components/shared/form/Input';
import MiniSelect from 'components/shared/MiniSelect';
import { SKILLED_NURSING_FACILITY } from 'constants/locationTypes';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { simpleDashDate } from 'lib/date';
import Workflow from 'models/Workflow';
import { GroupAccessMode, groupsQueryKeys, indexGroups } from 'services/api/group';
import { createWorkflow, WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { Body, BodySmall, H2, H3 } from 'styles/typography';

import { INBOUND_REFERRALS_ROUTES, REFERRAL_INTAKE_API_NAME } from './constants';

export default function NewInboundReferralPage() {
  const navigate = useNavigate();

  const [groupId, setGroupId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState<Date | null>(null);

  // The buildings this user can target: the SNFs in their allowed_groups (Phase 0
  // inbound referrals are to a skilled nursing facility). `accessModes: allowed`
  // mirrors what the API authorizes on create (design §14, API contract); `type`
  // filters by the group's group_type api_name.
  const groupsParams = useMemo(
    () => ({ accessModes: [GroupAccessMode.Allowed], type: SKILLED_NURSING_FACILITY, pageSize: 200, sortBy: 'name' }),
    []
  );
  const { data: groupsResult, isPending: groupsLoading } = useQuery({
    queryKey: [...groupsQueryKeys.index, groupsParams],
    queryFn: ({ signal }) => indexGroups(groupsParams, signal),
  });
  const groups = groupsResult?.data ?? [];

  const { mutate, isPending } = useMutateWithToast({
    mutationFn: createWorkflow,
    onSuccess: (workflow: Workflow) => navigate(`${INBOUND_REFERRALS_ROUTES.BASE}/${workflow.id}`),
  });

  const handleCreate = () => {
    if (!groupId) return;

    // Optional demographics seed — only include fields the user actually entered,
    // keyed by their declared (snake_case) value names.
    const values: WorkflowValuesInput = {};
    if (firstName.trim()) values.first_name = firstName.trim();
    if (lastName.trim()) values.last_name = lastName.trim();
    const dobString = simpleDashDate(dob);
    if (dobString) values.date_of_birth = dobString;

    mutate({
      workflowDefinitionApiName: REFERRAL_INTAKE_API_NAME,
      groupIds: [groupId],
      ...(Object.keys(values).length > 0 ? { values } : {}),
    });
  };

  return (
    <Page>
      <Page.Header>
        <HeaderInner>
          <H2>New inbound referral</H2>
          <BodySmall>Start a referral intake for one of your buildings.</BodySmall>
        </HeaderInner>
      </Page.Header>
      <Page.Content>
        <Form>
          <Field>
            <FieldLabel>Building</FieldLabel>
            <div data-testid='building-select'>
              <MiniSelect
                options={groups.map((group) => ({ label: group.name, value: group.id }))}
                value={groupId}
                onChange={(option) => setGroupId(option.value)}
              />
            </div>
            {!groupsLoading && groups.length === 0 && (
              <HelpText>You don’t have access to any buildings for inbound referrals.</HelpText>
            )}
          </Field>

          <Subhead>Demographics (optional)</Subhead>
          <Body>You can seed the patient’s details now, or enter them on the referral.</Body>
          <Fields>
            <Field>
              <FieldLabel>First name</FieldLabel>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid='new-first_name' />
            </Field>
            <Field>
              <FieldLabel>Last name</FieldLabel>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid='new-last_name' />
            </Field>
            <Field>
              <FieldLabel>Date of birth</FieldLabel>
              <DatePicker selected={dob} onChange={setDob} $fullWidth placeholder='MM/DD/YYYY' />
            </Field>
          </Fields>

          <Button
            onClick={handleCreate}
            disabled={!groupId || isPending}
            loading={isPending}
            data-testid='create-referral'>
            Create referral
          </Button>
        </Form>
      </Page.Content>
    </Page>
  );
}

const HeaderInner = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 640px;
`;

const Fields = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const Field = styled.div`
  min-width: 200px;
`;

const Subhead = styled(H3)`
  margin-top: 8px;
`;

const HelpText = styled(BodySmall)`
  margin-top: 4px;
  color: ${colors.accentRed};
`;
